import { Injectable, computed, effect, inject, signal } from "@angular/core";
import type {
	GameSessionDTO,
	GameSessionSummaryDTO,
	GameStartMode,
} from "@magpie/types";
import { GameSessionStatus } from "@magpie/types";
import { AuthService } from "./auth.service";
import { TimeService } from "./time.service";
import { TrpcService } from "./trpc.service";
import { environment } from "./environment";

export interface LiveSessionRef {
	sessionId: string;
	dashSlug: string;
	endsAt: string | null;
}

@Injectable({
	providedIn: "root",
})
export class GameSessionService {
	private readonly trpc = inject(TrpcService);
	private readonly auth = inject(AuthService);
	private readonly time = inject(TimeService);
	private static readonly LIVE_SESSION_KEY = "magpie:live_session";

	/**
	 * The session the user is currently playing/viewing. Populated by the
	 * game shell after getSession resolves; read by child routes so they
	 * can render static info (dash name, room code, team) while the
	 * PartyKit websocket is still connecting.
	 */
	readonly activeSession = signal<GameSessionDTO | null>(null);
	readonly isNewTeam = signal(false);
	readonly activeTeamId = signal<string | null>(null);
	readonly liveSessionRef = signal<LiveSessionRef | null>(null);

	/**
	 * In-flight sessions (Lobby + Active) the user is a participant in.
	 * Populated on auth-ready and refreshed on resume events. Consumed by
	 * the hunts-page banner and the live pill in the tab bar.
	 */
	readonly mySessions = signal<GameSessionSummaryDTO[]>([]);
	readonly mySessionsLoading = signal(false);

	readonly liveSession = computed<GameSessionSummaryDTO | null>(() => {
		const sessions = this.mySessions();
		const now = this.time.serverNow();
		const active = sessions.find(
			(s) =>
				s.status === GameSessionStatus.Active &&
				(!s.endsAt || new Date(s.endsAt).getTime() > now),
		);
		return active ?? null;
	});

	constructor() {
		try {
			const raw = localStorage.getItem(GameSessionService.LIVE_SESSION_KEY);
			if (raw) {
				const stored = JSON.parse(raw) as LiveSessionRef;
				if (
					!stored.endsAt ||
					new Date(stored.endsAt).getTime() > this.time.serverNow()
				) {
					this.liveSessionRef.set(stored);
				}
			}
		} catch {
			/* ignore */
		}

		effect(() => {
			if (this.auth.isAuthenticated()) {
				void this.loadMySessions();
			} else {
				this.mySessions.set([]);
			}
		});
	}

	setLiveSession(ref: LiveSessionRef): void {
		this.liveSessionRef.set(ref);
		try {
			localStorage.setItem(
				GameSessionService.LIVE_SESSION_KEY,
				JSON.stringify(ref),
			);
		} catch {
			/* ignore */
		}
	}

	clearLiveSession(): void {
		this.liveSessionRef.set(null);
		try {
			localStorage.removeItem(GameSessionService.LIVE_SESSION_KEY);
		} catch {
			/* ignore */
		}
	}

	async createSession(input: {
		dashId: string;
		teamName: string;
		startMode: GameStartMode;
		scheduledAt?: string;
	}): Promise<GameSessionDTO> {
		return this.trpc.client.gameSession.create.mutate(input);
	}

	async ensureSessionForDash(
		dashSlug: string,
	): Promise<{ session: GameSessionDTO; isNewTeam: boolean; teamId: string }> {
		return this.trpc.client.gameSession.ensureForSlug.mutate({ dashSlug });
	}

	async joinSession(
		roomCode: string,
		displayName: string,
	): Promise<GameSessionDTO> {
		return this.trpc.client.gameSession.join.mutate({ roomCode, displayName });
	}

	async startGame(sessionId: string): Promise<GameSessionDTO> {
		return this.trpc.client.gameSession.start.mutate({ sessionId });
	}

	async getSession(id: string): Promise<GameSessionDTO> {
		return this.trpc.client.gameSession.getById.query({ id });
	}

	async getSessionByRoomCode(roomCode: string): Promise<GameSessionDTO> {
		return this.trpc.client.gameSession.getByRoomCode.query({ roomCode });
	}

	async getMySessions(): Promise<GameSessionSummaryDTO[]> {
		return this.trpc.client.gameSession.listMySessions.query();
	}

	/**
	 * Fetch the user's in-flight sessions and cache them in the mySessions
	 * signal. Called on auth-ready, on window focus, and after join/start
	 * mutations. Safe to call when signed-out — returns an empty list.
	 */
	async loadMySessions(): Promise<GameSessionSummaryDTO[]> {
		if (!this.auth.isAuthenticated()) {
			this.mySessions.set([]);
			return [];
		}
		this.mySessionsLoading.set(true);
		try {
			const sessions = await this.getMySessions();
			this.mySessions.set(sessions);
			return sessions;
		} catch (err) {
			console.warn("Failed to load my sessions:", err);
			return this.mySessions();
		} finally {
			this.mySessionsLoading.set(false);
		}
	}

	/**
	 * DEV-ONLY: reset a session back to lobby (wipe submissions, clear
	 * timers) and force the PartyKit room to match. Used by the dev
	 * "Restart" control on the /complete page.
	 */
	async devResetSession(sessionId: string): Promise<GameSessionDTO> {
		const session = await this.trpc.client.gameSession.devReset.mutate({
			sessionId,
		});
		this.activeSession.set(session);

		const partyUrl = environment.partyUrl ?? "http://127.0.0.1:5511";
		try {
			await fetch(`${partyUrl}/party/${sessionId}?force=1`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					dashId: session.dashId,
					teamName: session.teamName,
					roomCode: session.roomCode,
					startMode: session.startMode,
					status: session.status,
					durationMinutes: session.durationMinutes,
					scheduledAt: session.scheduledAt,
					startedAt: session.startedAt,
					endsAt: session.endsAt,
					gameConfig: session.gameConfig,
					participants: session.participants.map((p) => ({
						userId: p.userId,
						displayName: p.displayName,
						joinedAt: p.joinedAt,
					})),
				}),
			});
		} catch (error) {
			console.warn("Failed to force-reset PartyKit room:", error);
		}

		return session;
	}
}
